import { useContext } from 'react';
import { useAccount } from '@micro-stacks/react';
import { request } from '@stacks/connect';
import {
  addPermissionsSignature,
  createPermissionsSignature,
  getAllMyContacts,
  getMultisignWalletById,
  login,
} from 'api';
import { useAtom } from 'jotai';
import { IEditWalletPermissions } from 'types';

import { CONNECT_MESSAGE } from '../constants/base';
import { AuthAddressContext } from '../Context/Context';
import { addressAtom } from '../store/address';
import { contactsAtom } from '../store/contacts';
import { ledgerDialogAtom } from '../store/ledger/ledger-dialog';
import { getLedgerStorage } from '../store/ledger/ledger-storage';
import { StxChainEnum } from '../types/enums';
import { getStorageKeysForNetwork } from '../utils/LocalstorageUtils';
import { resolveProvider } from '../utils/provider';

import useNetwork from './use-network';

export enum PROVIDERS {
  Xverse = 'Xverse',
  Leather = 'Leather',
  Ledger = 'Ledger',
}

const useData = () => {
  const [, setContacts] = useAtom(contactsAtom);
  const [, setAddress] = useAtom(addressAtom);
  const [, setLedgerDialog] = useAtom(ledgerDialogAtom);
  const STXaccount = useAccount();
  const [network] = useNetwork();
  const authContextWalletAddress = useContext(AuthAddressContext);

  const signMessage = (provider: PROVIDERS, message?: string, address?: string) => {
    if (!authContextWalletAddress && !address) return Promise.reject('No wallet address');

    if (provider === PROVIDERS.Ledger) {
      return new Promise<{ publicKey: string; signature: string }>((resolve, reject) => {
        const publicKey = getLedgerStorage().getLedgerInfo()?.publicKey ?? '';

        setLedgerDialog({
          isSignMessageOpened: true,
          message: message || CONNECT_MESSAGE,
          onSignSuccess: (signature: string) => {
            resolve({ publicKey, signature });
          },
        });
      });
    }

    const selectedProvider = resolveProvider(provider);

    return new Promise<{ publicKey: string; signature: string }>((resolve, reject) => {
      request({ provider: selectedProvider }, 'stx_signMessage', {
        message: message || CONNECT_MESSAGE,
      })
        .then((res) => {
          if (message) {
            resolve({
              publicKey: res.publicKey,
              signature: res.signature,
            });
          } else {
            login({
              network: StxChainEnum[network],
              publicKey: res.publicKey,
              signature: res.signature,
              address: STXaccount.stxAddress || '',
              setAddress,
            });
            resolve({
              publicKey: res.publicKey,
              signature: res.signature,
            });
          }
        })
        .catch(() => {
          reject('User cancelled');
        });
    });
  };

  const makeAuthRequest = async (provider: PROVIDERS) => {
    const selectedProvider = resolveProvider(provider);

    const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
      Promise.race([
        promise,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`${label}: no response after ${ms / 1000}s`)), ms)
        ),
      ]);

    await withTimeout(
      request({ provider: selectedProvider, forceWalletSelect: false }, 'stx_getAddresses', {}),
      30000,
      'stx_getAddresses'
    );
    localStorage.setItem(getStorageKeysForNetwork().walletType, provider);

    // Delay before second request -- mobile wallets need time to dismiss
    // the first approval modal before they can show another one
    await new Promise((r) => setTimeout(r, 1000));

    const res = await withTimeout(
      request({ provider: selectedProvider }, 'stx_signMessage', {
        message: CONNECT_MESSAGE,
      }),
      30000,
      'stx_signMessage'
    );

    login({
      network: StxChainEnum[network],
      publicKey: res.publicKey,
      signature: res.signature,
      address: STXaccount.stxAddress || '',
      setAddress,
    });
  };

  const getMultisignWalletDataById = async (id: string) => {
    if (!Boolean(id)) return null;
    const multisidData = await getMultisignWalletById(id);
    if (!multisidData.data) return null;

    return multisidData.data;
  };

  // TODO_R: [PN] rename to loadContacts
  const getContacts = async () => {
    if (!STXaccount) return;
    try {
      const contactsList = await getAllMyContacts();
      setContacts(contactsList.data);
    } catch (error) {
      console.error('Fetching contacts error:', error);
    }
  };

  const signPermissionsMessage = (
    vault: string,
    messageString: string,
    provider: PROVIDERS,
    onSign: () => void,
    permissionsData?: IEditWalletPermissions[]
  ) => {
    if (provider === PROVIDERS.Ledger) {
      setLedgerDialog({
        isSignMessageOpened: true,
        message: messageString,
        onSignSuccess: async (signature: string) => {
          permissionsData &&
            (await createPermissionsSignature(
              {
                vault: vault,
                message: messageString,
                permissions: permissionsData,
              },
              `stx:${network}`
            ));

          await addPermissionsSignature(
            {
              vault: vault,
              message: messageString,
              signature: signature,
            },
            StxChainEnum[network]
          );
          onSign();
        },
      });

      return;
    }

    const selectedProvider = resolveProvider(provider);
    request({ provider: selectedProvider }, 'stx_signMessage', {
      message: messageString,
    })
      .then(async (res) => {
        permissionsData &&
          (await createPermissionsSignature(
            {
              vault: vault,
              message: messageString,
              permissions: permissionsData,
            },
            `stx:${network}`
          ));

        await addPermissionsSignature(
          {
            vault: vault,
            message: messageString,
            signature: res.signature,
          },
          StxChainEnum[network]
        );
        onSign();
      })
      .catch(() => {
        // User cancelled
      });
  };

  return {
    signMessage,
    makeAuthRequest,
    getMultisignWalletDataById,
    getContacts,
    signPermissionsMessage,
  };
};

export default useData;
